import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Package,
  PackageCheck,
  Truck,
  Route,
  CircleAlert,
  Clock,
  CheckCircle2,
  PackageOpen,
  RotateCcw,
  Warehouse,
  ChevronRight,
  AlertTriangle,
  Ticket,
  Wallet,
  CalendarClock,
  Archive,
  type LucideIcon,
} from 'lucide-react';
import { LOCAL_EN, ROUTE } from '@/config';
import useLanguageStore from '@/stores/useLanguageStore';
import { useFetchAvailableCod, useFetchOrderCount, useFetchPendingTicketCount } from '@/hooksApi';
import usePermission from '@/hooks/usePermission';
import { PermissionName, OrderStatusCountResponse } from '@/types';
import { OrderStatusTab } from '@/types/columns';
import { Spin } from 'antd';
import { shortenNumber } from '@/utils';

// ── Color tokens (inline to avoid CSS var issues in portal contexts) ────────

const COLORS = {
  foreground: 'rgb(57, 57, 60)',
  mutedFg: 'rgb(115, 123, 140)',
  border: 'rgb(232, 230, 227)',
  cardBg: '#ffffff',
} as const;

// ── Status-to-pipeline mapping ──────────────────────────────────────────────

interface PipelineItem {
  labelKey: string;
  statusKey: string;
  tab: string;
  subTab?: string;
  Icon: LucideIcon;
  iconBg: string;
  iconColor: string;
}

interface PipelineCategory {
  key: string;
  labelKey: string;
  tab: string;
  accent: string;
  Icon: LucideIcon;
  tinted?: boolean;
  getTotal: (counts: OrderStatusCountResponse) => number;
  items: (PipelineItem & { getValue: (counts: OrderStatusCountResponse) => number })[];
}

const PIPELINE: PipelineCategory[] = [
  // ── With You = Orders page "Ready to Ship" tab ──
  {
    key: 'with-you',
    labelKey: 'with_you',
    tab: 'ready_to_ship',
    accent: 'hsl(268, 55%, 52%)',
    Icon: Package,
    getTotal: (c) => c[OrderStatusTab.READY_TO_SHIP]?.total ?? 0,
    items: [
      {
        labelKey: 'new',
        statusKey: 'new',
        tab: 'ready_to_ship',
        subTab: 'new',
        Icon: Package,
        iconBg: 'hsl(268, 60%, 93%)',
        iconColor: 'hsl(268, 55%, 52%)',
        getValue: (c) => c[OrderStatusTab.READY_TO_SHIP]?.new ?? 0,
      },
      {
        labelKey: 'scheduled',
        statusKey: 'scheduled',
        tab: 'ready_to_ship',
        subTab: 'scheduled',
        Icon: CalendarClock,
        iconBg: 'hsl(210, 65%, 93%)',
        iconColor: 'hsl(210, 60%, 42%)',
        getValue: (c) => c[OrderStatusTab.READY_TO_SHIP]?.scheduled ?? 0,
      },
    ],
  },
  // ── In Progress = Orders page "In Progress" tab ──
  {
    key: 'in-progress',
    labelKey: 'in_progress',
    tab: 'in_progress',
    accent: 'hsl(195, 65%, 40%)',
    Icon: Truck,
    getTotal: (c) => c[OrderStatusTab.IN_PROGRESS]?.total ?? 0,
    items: [
      {
        labelKey: 'collecting',
        statusKey: 'collecting',
        tab: 'in_progress',
        subTab: 'collecting',
        Icon: PackageCheck,
        iconBg: 'hsl(30, 75%, 93%)',
        iconColor: 'hsl(30, 70%, 42%)',
        getValue: (c) => c[OrderStatusTab.IN_PROGRESS]?.collecting ?? 0,
      },
      {
        labelKey: 'collected',
        statusKey: 'collected',
        tab: 'in_progress',
        subTab: 'collected',
        Icon: PackageCheck,
        iconBg: 'hsl(95, 55%, 92%)',
        iconColor: 'hsl(95, 50%, 40%)',
        getValue: (c) => c[OrderStatusTab.IN_PROGRESS]?.collected ?? 0,
      },
      {
        labelKey: 'in_transit',
        statusKey: 'in_transit',
        tab: 'in_progress',
        subTab: 'en-route',
        Icon: Truck,
        iconBg: 'hsl(195, 70%, 92%)',
        iconColor: 'hsl(195, 65%, 40%)',
        getValue: (c) => c[OrderStatusTab.IN_PROGRESS]?.in_transit ?? 0,
      },
      {
        labelKey: 'out_for_delivery',
        statusKey: 'out_for_delivery',
        tab: 'in_progress',
        subTab: 'out-for-delivery',
        Icon: Route,
        iconBg: 'hsl(145, 60%, 92%)',
        iconColor: 'hsl(145, 55%, 38%)',
        getValue: (c) => c[OrderStatusTab.IN_PROGRESS]?.out_for_delivery ?? 0,
      },
      {
        labelKey: 'postponed',
        statusKey: 'postponed',
        tab: 'in_progress',
        subTab: 'postponed',
        Icon: Clock,
        iconBg: 'hsl(40, 90%, 92%)',
        iconColor: 'hsl(40, 80%, 40%)',
        getValue: (c) => c[OrderStatusTab.IN_PROGRESS]?.postponed ?? 0,
      },
      {
        labelKey: 'hold_resolved',
        statusKey: 'hold_resolved',
        tab: 'in_progress',
        subTab: 'hold-resolved',
        Icon: PackageCheck,
        iconBg: 'hsl(145, 60%, 92%)',
        iconColor: 'hsl(145, 55%, 38%)',
        getValue: (c) => c[OrderStatusTab.IN_PROGRESS]?.hold_resolved ?? 0,
      },
    ],
  },
  // ── On Hold = Orders page "On Hold" tab (only 2 merchant-visible sub-filters) ──
  {
    key: 'on-hold',
    labelKey: 'on_hold',
    tab: 'on_hold',
    accent: 'hsl(0, 65%, 50%)',
    Icon: CircleAlert,
    tinted: true,
    getTotal: (c) => c[OrderStatusTab.ON_HOLD]?.total ?? 0,
    items: [
      {
        labelKey: 'on_hold',
        statusKey: 'on_hold',
        tab: 'on_hold',
        subTab: 'on-hold',
        Icon: CircleAlert,
        iconBg: 'hsl(35, 85%, 92%)',
        iconColor: 'hsl(35, 75%, 38%)',
        getValue: (c) => {
          const oh = c[OrderStatusTab.ON_HOLD];
          return (oh?.on_hold ?? 0) + (oh?.retry_request ?? 0);
        },
      },
      {
        labelKey: 'exceptions',
        statusKey: 'exception',
        tab: 'on_hold',
        subTab: 'exception',
        Icon: AlertTriangle,
        iconBg: 'hsl(0, 70%, 94%)',
        iconColor: 'hsl(0, 65%, 45%)',
        getValue: (c) => c[OrderStatusTab.ON_HOLD]?.exception ?? 0,
      },
    ],
  },
  // ── RTO = Orders page "RTO" tab ──
  {
    key: 'rto',
    labelKey: 'rto',
    tab: 'return_to_origin',
    accent: 'hsl(25, 65%, 42%)',
    Icon: RotateCcw,
    getTotal: (c) => c[OrderStatusTab.RTO]?.total ?? 0,
    items: [
      {
        labelKey: 'rto_request',
        statusKey: 'rto_requested',
        tab: 'return_to_origin',
        subTab: 'rto-requested',
        Icon: RotateCcw,
        iconBg: 'hsl(15, 70%, 93%)',
        iconColor: 'hsl(15, 65%, 42%)',
        getValue: (c) => c[OrderStatusTab.RTO]?.rto_requested ?? 0,
      },
      {
        labelKey: 'returned_to_warehouse',
        statusKey: 'returned_warehouse',
        tab: 'return_to_origin',
        subTab: 'rto-received-warehouse',
        Icon: Warehouse,
        iconBg: 'hsl(25, 75%, 92%)',
        iconColor: 'hsl(25, 65%, 42%)',
        getValue: (c) => c[OrderStatusTab.RTO]?.returned_warehouse ?? 0,
      },
      {
        labelKey: 'return_in_progress',
        statusKey: 'rto_in_progress',
        tab: 'return_to_origin',
        subTab: 'rto-in-transit',
        Icon: Truck,
        iconBg: 'hsl(355, 55%, 92%)',
        iconColor: 'hsl(355, 50%, 42%)',
        getValue: (c) => (c[OrderStatusTab.RTO]?.rto_scheduled ?? 0) + (c[OrderStatusTab.RTO]?.returning_origin ?? 0),
      },
    ],
  },
];

// ── Dashboard Page ──────────────────────────────────────────────────────────

const Dashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();

  const { language } = useLanguageStore();
  const isArabic = language !== LOCAL_EN;

  const { data: orderCountData, isLoading: loadingOrders } = useFetchOrderCount();
  const { data: availableCodData, isLoading: loadingCod } = useFetchAvailableCod(
    hasPermission(PermissionName.FINANCE),
  );
  const { data: ticketCountData } = useFetchPendingTicketCount(
    hasPermission(PermissionName.CREATE_TICKET),
  );

  const counts = orderCountData?.object;
  const availableCod = availableCodData?.object?.['available-cod'] ?? 0;
  const pendingCod = availableCodData?.object?.['pending-cod'] ?? 0;

  const actionRequiredCount = counts?.[OrderStatusTab.ON_HOLD]?.action_required ?? 0;
  const pendingTicketCount = (ticketCountData?.object as any)?.total_pending ?? ticketCountData?.object?.total ?? 0;

  const goOrders = (tab?: string, subTab?: string) => {
    const params = new URLSearchParams();
    if (tab) params.set('activeTab', tab);
    if (subTab) params.set('activeSubTab', subTab);
    const qs = params.toString();
    navigate(qs ? `${ROUTE.ORDERS}?${qs}` : ROUTE.ORDERS);
  };

  // Completed stats
  const delivered = counts?.[OrderStatusTab.FINISHED]?.delivered ?? 0;
  const returned = counts?.[OrderStatusTab.FINISHED]?.returned ?? 0;
  const partReturned = counts?.[OrderStatusTab.FINISHED]?.partially_returned ?? 0;
  const partDelivered = counts?.[OrderStatusTab.FINISHED]?.partially_delivered ?? 0;
  const resendCount = counts?.[OrderStatusTab.FINISHED]?.resend ?? 0;
  const totalCompleted = counts?.[OrderStatusTab.FINISHED]?.total ?? (delivered + returned + partReturned + partDelivered + resendCount);

  // Date string — Arabic uses Iraqi locale
  const now = new Date();
  const dateStr = now.toLocaleDateString(isArabic ? 'ar-IQ' : 'en-US', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  if (loadingOrders) {
    return (
      <div className='flex items-center justify-center h-64'>
        <Spin size='large' />
      </div>
    );
  }

  return (
    <div className='space-y-5'>
      {/* ── Date subtitle ──────────────────────────────────────────────── */}
      <p className='text-[12px] -mt-4' style={{ color: COLORS.mutedFg }}>
        {dateStr}
      </p>

      {/* ── 1. PRIORITY ALERTS ─────────────────────────────────────────── */}
      {(pendingTicketCount > 0 || actionRequiredCount > 0) && (
        <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
          {/* QA-6: Replaced On Hold orders banner with Waiting Tickets banner */}
          {pendingTicketCount > 0 && (
            <button
              onClick={() => navigate(ROUTE.TICKET_CENTER, { state: { initialTab: 'pending' } })}
              className='rounded-2xl border border-solid px-4 py-3.5 flex items-center gap-3 cursor-pointer transition-colors text-start hover:bg-amber-50/80 active:scale-[0.99]'
              style={{
                borderColor: COLORS.border,
                borderInlineStartWidth: 4,
                borderInlineStartColor: 'hsl(35,75%,42%)',
                background: 'hsla(35,75%,42%,0.05)',
              }}
            >
              <div
                className='w-9 h-9 rounded-full flex items-center justify-center shrink-0'
                style={{ background: 'hsla(35,75%,42%,0.12)' }}
              >
                <Ticket className='w-4 h-4' style={{ color: 'hsl(35,75%,42%)' }} />
              </div>
              <div className='flex-1 min-w-0'>
                <p className='text-[14px] font-bold m-0' style={{ color: COLORS.foreground }}>
                  <span className='text-[20px]'>{shortenNumber(pendingTicketCount)}</span> {String(t('waiting_tickets', { defaultValue: 'Waiting Tickets' }))}
                </p>
                <p className='text-[11px] mt-0.5 m-0' style={{ color: COLORS.mutedFg }}>
                  {String(t('tickets_need_attention', { defaultValue: 'Tickets awaiting your response' }))}
                </p>
              </div>
              <ChevronRight className='w-4 h-4 shrink-0' style={{ color: COLORS.mutedFg }} />
            </button>
          )}
          {actionRequiredCount > 0 && (
            <button
              onClick={() => goOrders('on_hold', 'action-required')}
              className='rounded-2xl border border-solid px-4 py-3.5 flex items-center gap-3 cursor-pointer transition-colors text-start hover:bg-orange-50/80 active:scale-[0.99]'
              style={{
                borderColor: COLORS.border,
                borderInlineStartWidth: 4,
                borderInlineStartColor: 'hsl(25,85%,50%)',
                background: 'hsla(25,85%,50%,0.05)',
              }}
            >
              <div
                className='w-9 h-9 rounded-full flex items-center justify-center shrink-0'
                style={{ background: 'hsla(25,85%,50%,0.12)' }}
              >
                <CircleAlert className='w-4 h-4' style={{ color: 'hsl(25,85%,50%)' }} />
              </div>
              <div className='flex-1 min-w-0'>
                <p className='text-[14px] font-bold m-0' style={{ color: COLORS.foreground }}>
                  <span className='text-[20px]'>{shortenNumber(actionRequiredCount)}</span> {String(t('action_required', { defaultValue: 'Action Required' }))}
                </p>
                <p className='text-[11px] mt-0.5 m-0' style={{ color: COLORS.mutedFg }}>
                  {String(t('require_immediate_resolution'))}
                </p>
              </div>
              <ChevronRight className='w-4 h-4 shrink-0' style={{ color: COLORS.mutedFg }} />
            </button>
          )}

        </div>
      )}

      {/* ── 2. BALANCE CARDS ───────────────────────────────────────────── */}
      {hasPermission(PermissionName.FINANCE) && (
        <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
          <button
            onClick={() => navigate(ROUTE.FINANCE)}
            className='rounded-2xl border border-solid p-5 text-start transition-all hover:border-gray-400/50 active:scale-[0.99]'
            style={{ backgroundColor: COLORS.cardBg, borderColor: COLORS.border }}
          >
            <div className='flex items-center gap-2 mb-3'>
              <Wallet className='w-4 h-4' style={{ color: 'hsl(40, 75%, 42%)' }} />
              <span className='text-[13px] font-medium' style={{ color: COLORS.mutedFg }}>
                {String(t('available_balance'))}
              </span>
            </div>
            <div className='flex items-baseline gap-1.5'>
              <span className='text-[26px] font-bold leading-none' style={{ color: COLORS.foreground }}>
                {loadingCod ? '...' : availableCod.toLocaleString()}
              </span>
              <span className='text-[13px] font-medium' style={{ color: COLORS.mutedFg }}>
                {String(t('iqd'))}
              </span>
            </div>
          </button>

          <div
            className='rounded-2xl border border-solid p-5'
            style={{ backgroundColor: COLORS.cardBg, borderColor: COLORS.border }}
          >
            <div className='flex items-center gap-2 mb-3'>
              <Clock className='w-4 h-4' style={{ color: 'hsl(30, 80%, 45%)' }} />
              <span className='text-[13px] font-medium' style={{ color: COLORS.mutedFg }}>
                {String(t('pending_balance'))}
              </span>
            </div>
            <div className='flex items-baseline gap-1.5'>
              <span className='text-[26px] font-bold leading-none' style={{ color: COLORS.foreground }}>
                {loadingCod ? '...' : pendingCod.toLocaleString()}
              </span>
              <span className='text-[13px] font-medium' style={{ color: COLORS.mutedFg }}>
                {String(t('iqd'))}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ── 3. ACTIVE ORDERS PIPELINE ──────────────────────────────────── */}
      {counts && (
        <div>
          <div className='flex items-center justify-between mb-3'>
            <h2
              className='text-[11px] font-bold uppercase tracking-wider m-0'
              style={{ color: COLORS.mutedFg }}
            >
              {String(t('active_orders'))}
            </h2>
            <button
              onClick={() => goOrders()}
              className='flex items-center gap-0.5 text-[12px] font-medium transition-colors bg-transparent border-0 cursor-pointer p-0 text-gray-400 hover:text-gray-600'
            >
              {String(t('view_all'))} <ChevronRight className='w-3.5 h-3.5' />
            </button>
          </div>

          <div
            className='rounded-2xl border border-solid overflow-hidden'
            style={{ backgroundColor: COLORS.cardBg, borderColor: COLORS.border }}
          >
            <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4' style={{ gridTemplateColumns: `repeat(${PIPELINE.length}, 1fr)` }}>
              {PIPELINE.map((cat, catIdx) => {
                const total = cat.getTotal(counts);
                return (
                  <div
                    key={cat.key}
                    className='flex flex-col'
                    style={{
                      ...(cat.tinted ? { background: 'hsla(0,70%,55%,0.03)' } : {}),
                      ...(catIdx > 0
                        ? { borderInlineStart: `1px solid ${COLORS.border}` }
                        : {}),
                    }}
                  >
                    {/* Column header */}
                    <button
                      onClick={() => goOrders(cat.tab)}
                      className='flex items-center justify-between px-4 pt-4 pb-2 w-full bg-transparent border-0 cursor-pointer transition-colors hover:bg-black/[0.03]'
                    >
                      <div className='flex items-center gap-2'>
                        <div
                          className='w-2 h-2 rounded-full shrink-0'
                          style={{ background: cat.accent }}
                        />
                        <span
                          className='text-[11px] font-bold uppercase tracking-wider'
                          style={{ color: COLORS.mutedFg }}
                        >
                          {String(t(cat.labelKey))}
                        </span>
                      </div>
                      <span
                        className='text-[20px] font-bold leading-none'
                        style={{ color: COLORS.foreground }}
                      >
                        {shortenNumber(total)}
                      </span>
                    </button>

                    {/* Sub-statuses */}
                    <div className='flex-1 px-2 pb-2'>
                      {cat.items.map((item) => {
                        const val = item.getValue(counts);
                        return (
                          <button
                            key={item.statusKey}
                            onClick={() => goOrders(item.tab, item.subTab)}
                            className='w-full flex items-center justify-between px-2 py-2 rounded-lg transition-colors bg-transparent border-0 cursor-pointer hover:bg-black/[0.04]'
                            style={{ color: COLORS.foreground }}
                          >
                            <div className='flex items-center gap-2'>
                              <div
                                className='w-6 h-6 rounded-full flex items-center justify-center shrink-0'
                                style={{ background: item.iconBg }}
                              >
                                <item.Icon className='w-3 h-3' style={{ color: item.iconColor }} />
                              </div>
                              <span className='text-[12px] font-medium'>
                                {String(t(item.labelKey))}
                              </span>
                            </div>
                            <span className='text-[12px] font-semibold'>
                              {shortenNumber(val)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── 4. COMPLETED + INVENTORY FOOTER ──────────────────────────── */}
      {counts && (
        <div className='flex items-center justify-between px-1'>
          <div className='flex items-center gap-2'>
            <CheckCircle2 className='w-3.5 h-3.5 shrink-0' style={{ color: 'rgba(115,123,140,0.5)' }} />
            <div className='flex items-center gap-1.5 flex-wrap'>
              <span className='text-[12px]' style={{ color: COLORS.mutedFg }}>
                <span className='font-semibold'>{shortenNumber(totalCompleted)}</span> {String(t('completed'))}
              </span>
              <span className='text-[10px]' style={{ color: COLORS.mutedFg }}>·</span>
              <span
                className='text-[12px] cursor-pointer transition-colors text-gray-400 hover:text-gray-500'
                onClick={() => goOrders('finished', 'delivered')}
              >
                {shortenNumber(delivered)} {String(t('delivered')).toLowerCase()}
              </span>
              <span className='text-[10px]' style={{ color: COLORS.mutedFg }}>·</span>
              <span
                className='text-[12px] cursor-pointer transition-colors text-gray-400 hover:text-gray-500'
                onClick={() => goOrders('finished', 'returned')}
              >
                {shortenNumber(returned)} {String(t('returned_to_you')).toLowerCase()}
              </span>
              {partReturned > 0 && (
                <>
                  <span className='text-[10px]' style={{ color: COLORS.mutedFg }}>·</span>
                  <span
                    className='text-[12px] cursor-pointer transition-colors text-gray-400 hover:text-gray-500'
                    onClick={() => goOrders('finished', 'partially_returned')}
                  >
                    {shortenNumber(partReturned)} {String(t('part_returned')).toLowerCase()}
                  </span>
                </>
              )}
            </div>
          </div>
          {(counts[OrderStatusTab.INVENTORY]?.total ?? 0) > 0 && (
            <button
              onClick={() => goOrders('inventory')}
              className='flex items-center gap-1.5 text-[12px] cursor-pointer transition-colors bg-transparent border-0 p-0'
              style={{ color: COLORS.mutedFg }}
            >
              <Archive className='w-3.5 h-3.5 shrink-0' style={{ color: 'hsl(35,65%,45%)' }} />
              <span className='font-semibold' style={{ color: 'hsl(35,65%,40%)' }}>
                {shortenNumber(counts[OrderStatusTab.INVENTORY]?.total ?? 0)}
              </span>
              <span className='hover:text-gray-600'>{String(t('inventory')).toLowerCase()}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
